import React, { useState } from 'react';
import { X, Loader2, Sparkles, TriangleAlert, Check, Info } from 'lucide-react';
import { planningService } from '../../services/planningService';

// =============================================================================
// Planning > Network > Optimise
//
// Proposes the cheapest way to meet every site's demand, then lets the planner
// accept it LANE BY LANE. Nothing is written until a lane is ticked and Apply
// is pressed.
//
// WHY A DIFF AND NOT A RESULT. The optimiser is right about arithmetic and has
// no opinion about anything else — a contract that has to be honoured, a depot
// nobody wants single-sourced, a lane somebody has already promised. So it
// argues one lane at a time and the planner keeps the pen. "Cheapest" is an
// input to the decision, not the decision.
//
// THE COST COMPARISON IS THE EASIEST THING ON THIS SCREEN TO GET WRONG.
// On real data the current plan can be far CHEAPER than the optimal one —
// because it does not deliver. Printing "-R1 481 393" against a plan that
// serves demand the current one abandons would be a lie told in a red font. So
// when the two plans do not deliver the same thing (`summary.comparable`), the
// difference is labelled as the PRICE OF SERVING the shortfall, never a saving.
//
// ACCEPTING A LANE REPLACES IT OUTRIGHT, including emptying it. A lane the
// optimiser wants to stop using shows a proposed zero and is ticked like any
// other — that is a decision, not an omission, so it is on screen.
// =============================================================================

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const money = (n) => `R${fmt(n)}`;

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';

export default function OptimiseModal({ onClose, capacityUom = 'PALLET', onApplied }) {
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const [accepted, setAccepted] = useState(() => new Set());
  const [applied, setApplied] = useState(null);

  // Off by default. A share cap makes the plan dearer on purpose, so it is a
  // choice the planner makes knowingly rather than a default they inherit.
  const [capOn, setCapOn] = useState(false);
  const [cap, setCap] = useState(70);

  const uom = (capacityUom || 'PALLET').toLowerCase();
  const options = capOn ? { maxLaneSharePct: Number(cap) || 70 } : {};

  const run = async () => {
    setBusy(true); setError(''); setApplied(null);
    try {
      const res = await planningService.optimise(options);
      if (!res.success) {
        setPlan(null);
        setError(res.message || 'The optimiser could not produce a plan.');
        return;
      }
      setPlan(res);
      // Nothing pre-ticked. A screen that arrives with every lane accepted is a
      // silent overwrite wearing a checkbox.
      setAccepted(new Set());
    } catch (e) {
      setPlan(null);
      setError(e.response?.data?.message || e.message || 'The optimiser failed.');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!plan || !accepted.size) return;
    setApplying(true); setError('');
    try {
      const res = await planningService.applyOptimisation([...accepted], plan.plan_hash, options);
      setApplied(res);
      setPlan(null);
      setAccepted(new Set());
      onApplied?.();
    } catch (e) {
      const data = e.response?.data;
      setError(data?.code === 'PLAN_CHANGED'
        ? `${data.message}`
        : data?.message || e.message || 'The plan could not be applied.');
      // A stale plan must not stay on screen looking acceptable.
      if (data?.code === 'PLAN_CHANGED') { setPlan(null); setAccepted(new Set()); }
    } finally {
      setApplying(false);
    }
  };

  const toggle = (laneId) => setAccepted((prev) => {
    const next = new Set(prev);
    if (next.has(laneId)) next.delete(laneId); else next.add(laneId);
    return next;
  });

  const changed = (plan?.lanes || []).filter((l) => Math.abs(l.proposed_units - l.current_units) > 0.005);
  const allOn = changed.length > 0 && changed.every((l) => accepted.has(l.lane_id));
  const s = plan?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[88vh] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="m-0 text-base font-bold text-slate-900 dark:text-slate-100">Optimise the network</h2>
            <p className="m-0 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
              Meet every site&rsquo;s demand at the lowest total transfer cost. Nothing is saved until you accept it.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ---- controls ---- */}
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-4">
          <button className={btnPrimary} onClick={run} disabled={busy || applying}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {plan ? 'Run again' : 'Run optimiser'}
          </button>

          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={capOn} onChange={(e) => setCapOn(e.target.checked)}
                   className="rounded border-slate-300 dark:border-slate-600" />
            Cap any one lane at
            <input type="number" min={10} max={99} value={cap} disabled={!capOn}
                   onChange={(e) => setCap(e.target.value)}
                   className="w-14 px-1.5 py-0.5 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 disabled:opacity-40" />
            % of a site&rsquo;s inbound
          </label>
          <span className="text-[11px] text-slate-400 dark:text-slate-500">
            Stops a depot being single-sourced. It costs more on purpose.
          </span>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4 text-xs text-slate-600 dark:text-slate-300">

          {error && (
            <div className="px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10 flex gap-2">
              <TriangleAlert className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="m-0 text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {applied && (
            <div className="px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
              <p className="m-0 text-emerald-800 dark:text-emerald-300">
                {applied.message}
                {applied.lanesEmptied?.length > 0 && ` ${applied.lanesEmptied.length} lane(s) were emptied.`}
              </p>
            </div>
          )}

          {!plan && !busy && !applied && !error && (
            <p className="m-0 text-slate-500 dark:text-slate-400">
              Run the optimiser to see what it would change. It reads the production plan, site demand,
              opening stock and the inter-depot rate card, and proposes how much moves down each lane in
              each period.
            </p>
          )}

          {busy && (
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Solving&hellip;
            </div>
          )}

          {plan && s && (
            <>
              {/* ---- summary ---- */}
              <div className="grid grid-cols-2 gap-3">
                <Panel title="Plan today"
                       cost={s.currentCost}
                       short={s.currentUnmetUnits}
                       shortSites={s.currentUnmetSites} />
                <Panel title="Proposed" highlight
                       cost={s.proposedCost}
                       short={s.unmetUnits}
                       shortSites={s.unmetSites} />
              </div>

              <div className={`px-3 py-2 rounded-lg border ${s.comparable
                ? (s.saving >= 0
                  ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10'
                  : 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10')
                : 'border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-700/40'}`}>
                {s.comparable ? (
                  <p className="m-0 font-medium">
                    {s.saving >= 0
                      ? `Both plans deliver the same demand. The proposal costs ${money(s.saving)} less.`
                      : `Both plans deliver the same demand. The proposal costs ${money(-s.saving)} MORE — the constraints you set are worth that much.`}
                  </p>
                ) : (
                  <p className="m-0">
                    <strong className="font-semibold">These two are not the same plan.</strong>{' '}
                    Today&rsquo;s costs {money(s.currentCost)} and leaves {fmt(s.currentUnmetUnits)} units
                    undelivered; the proposal costs {money(s.proposedCost)} and leaves {fmt(s.unmetUnits)}.
                    The difference is what it costs to actually serve that demand, not a saving forgone.
                  </p>
                )}
              </div>

              {/* ---- lane diff ---- */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="m-0 font-bold text-slate-700 dark:text-slate-200">
                    {changed.length} lane{changed.length === 1 ? '' : 's'} would change
                  </p>
                  {changed.length > 0 && (
                    <button className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                            onClick={() => setAccepted(allOn ? new Set() : new Set(changed.map((l) => l.lane_id)))}>
                      {allOn ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400">
                      <tr>
                        <th className="w-8 px-2 py-1.5" />
                        <th className="px-2 py-1.5 text-left font-medium">Lane</th>
                        <th className="px-2 py-1.5 text-right font-medium">Now ({uom}s)</th>
                        <th className="px-2 py-1.5 text-right font-medium">Proposed</th>
                        <th className="px-2 py-1.5 text-right font-medium">Cost now</th>
                        <th className="px-2 py-1.5 text-right font-medium">Cost proposed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changed.map((l) => (
                        <tr key={l.lane_id}
                            className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40">
                          <td className="px-2 py-1.5 text-center">
                            <input type="checkbox" checked={accepted.has(l.lane_id)}
                                   onChange={() => toggle(l.lane_id)}
                                   className="rounded border-slate-300 dark:border-slate-600" />
                          </td>
                          <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200">
                            {l.origin_site_code} &rarr; {l.dest_site_code}
                            {l.approximated && (
                              <span title="Priced per trip or per km, so phase 1 spreads the charge over a full vehicle. Thin lanes are understated."
                                    className="ml-1.5 text-amber-600 dark:text-amber-400">~</span>
                            )}
                            {l.proposed_units <= 0.005 && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">emptied</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmt(l.current_units)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-medium text-slate-800 dark:text-slate-100">{fmt(l.proposed_units)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{money(l.current_cost)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{money(l.proposed_cost)}</td>
                        </tr>
                      ))}
                      {!changed.length && (
                        <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-400">
                          The current plan is already optimal under these settings.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {changed.some((l) => l.approximated) && (
                  <p className="m-0 mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                    ~ Lanes priced per trip or per km are approximated: the charge is spread across a full
                    vehicle, so a lane running part loads costs more in reality than shown.
                  </p>
                )}
              </div>

              {/* ---- shortfall ---- */}
              {plan.unmet?.length > 0 && (
                <Collapsible title={`${fmt(s.unmetUnits)} units cannot be delivered by any plan`} tone="amber">
                  <p className="m-0 mb-1.5">
                    Nothing in the network can supply these — they are a production or sourcing gap, not a
                    routing one.
                  </p>
                  <ul className="m-0 pl-4 list-disc space-y-0.5">
                    {plan.unmet.slice(0, 12).map((u, i) => (
                      <li key={i}>{u.site_code} &middot; {u.stock_code} &middot; P{u.financial_period} &mdash; {fmt(u.quantity)}</li>
                    ))}
                  </ul>
                  {plan.unmetTruncated && <p className="m-0 mt-1 italic">List truncated.</p>}
                </Collapsible>
              )}

              {/* ---- what was left out ---- */}
              {plan.meta?.excludedLanes?.length > 0 && (
                <Collapsible title={`${plan.meta.excludedLanes.length} lane(s) were left out of the plan`} tone="slate">
                  <ul className="m-0 pl-4 list-disc space-y-0.5">
                    {plan.meta.excludedLanes.map((e, i) => (
                      <li key={i}>
                        {e.origin_site_code ? `${e.origin_site_code} → ${e.dest_site_code}` : e.lane_id} &mdash; {e.reason}
                      </li>
                    ))}
                  </ul>
                  <p className="m-0 mt-1.5">
                    An unpriced lane is excluded rather than treated as free &mdash; a zero-cost lane would
                    absorb the whole network and produce a plan that looks fine and is not.
                  </p>
                </Collapsible>
              )}

              {plan.meta?.warnings?.length > 0 && (
                <Collapsible title={`${plan.meta.warnings.length} warning(s)`} tone="slate">
                  <ul className="m-0 pl-4 list-disc space-y-0.5">
                    {plan.meta.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </Collapsible>
              )}

              <p className="m-0 flex items-start gap-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                Accepting a lane REPLACES its whole plan &mdash; every SKU and period on it, including
                clearing lanes the optimiser wants emptied. Solved in {s.elapsedMs}ms.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            {plan ? `${accepted.size} of ${changed.length} lane(s) accepted` : ''}
          </span>
          <div className="flex items-center gap-2">
            <button className={btn} onClick={onClose}>Close</button>
            <button className={btnPrimary} onClick={apply} disabled={!plan || !accepted.size || applying}>
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Apply {accepted.size || ''} lane{accepted.size === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Panel = ({ title, cost, short, shortSites, highlight }) => (
  <div className={`px-3 py-2.5 rounded-lg border ${highlight
    ? 'border-blue-300 bg-blue-50 dark:border-blue-500/40 dark:bg-blue-500/10'
    : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-700/40'}`}>
    <p className="m-0 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</p>
    <p className="m-0 mt-0.5 text-lg font-bold tabular-nums text-slate-900 dark:text-slate-100">{money(cost)}</p>
    <p className={`m-0 mt-0.5 text-[11px] ${short > 0.5 ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
      {short > 0.5
        ? `${fmt(short)} units short across ${shortSites} site${shortSites === 1 ? '' : 's'}`
        : 'All demand met'}
    </p>
  </div>
);

const Collapsible = ({ title, tone, children }) => {
  const [open, setOpen] = useState(false);
  const tones = {
    amber: 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300',
    slate: 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-700/40',
  };
  return (
    <div className={`rounded-lg border ${tones[tone] || tones.slate}`}>
      <button className="w-full px-3 py-2 text-left font-medium" onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} {title}
      </button>
      {open && <div className="px-3 pb-2.5">{children}</div>}
    </div>
  );
};
